import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { devicesApi } from '@/api/devicesApi';
import type {
  CreateDeviceRequest,
  DeviceListRequest,
  QuickCommandRequest,
  UpdateDeviceRequest,
} from '@/types/device';

const KEYS = {
  all: ['devices'] as const,
  list: (f: DeviceListRequest) => [...KEYS.all, 'list', f] as const,
  detail: (id: number) => [...KEYS.all, 'detail', id] as const,
  detailExtended: (id: number) => [...KEYS.all, 'detail-extended', id] as const,
  commands: (id: number, page: number, pageSize: number) => [...KEYS.all, 'commands', id, page, pageSize] as const,
  audit: (id: number, page: number, pageSize: number) => [...KEYS.all, 'audit', id, page, pageSize] as const,
};

export function useDevices(filters: DeviceListRequest = {}) {
  return useQuery({ queryKey: KEYS.list(filters), queryFn: () => devicesApi.list(filters) });
}

export function useDevice(id: number | null) {
  return useQuery({
    queryKey: KEYS.detail(id ?? 0),
    queryFn: () => devicesApi.getById(id!),
    enabled: id != null,
  });
}

/**
 * Detalle extendido (apps, ubicacion, hardware). Solo se carga cuando el componente
 * de DeviceDetailPage esta montado. Refetch cada 30s para que se actualicen los datos
 * cuando el job background sincroniza desde Google.
 */
export function useDeviceDetail(id: number | null) {
  return useQuery({
    queryKey: KEYS.detailExtended(id ?? 0),
    queryFn: () => devicesApi.getDetail(id!),
    enabled: id != null,
    refetchInterval: 30_000,
  });
}

export function useDeviceCommands(id: number | null, page = 1, pageSize = 20) {
  return useQuery({
    queryKey: KEYS.commands(id ?? 0, page, pageSize),
    queryFn: () => devicesApi.listCommands(id!, page, pageSize),
    enabled: id != null,
    refetchInterval: 5_000, // refresh rapido para ver el avance de comandos en vivo
  });
}

export function useDeviceAudit(id: number | null, page = 1, pageSize = 50) {
  return useQuery({
    queryKey: KEYS.audit(id ?? 0, page, pageSize),
    queryFn: () => devicesApi.listAudit(id!, page, pageSize),
    enabled: id != null,
  });
}

export function useQuickCommand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: number; request: QuickCommandRequest }) =>
      devicesApi.quickCommand(id, request),
    onSuccess: (_, { id }) => {
      // Invalidar lista de comandos del device y la lista global de devices (pendingCommandsCount)
      qc.invalidateQueries({ queryKey: KEYS.all });
      qc.invalidateQueries({ queryKey: ['commands'] });
    },
  });
}

export function useCreateDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (r: CreateDeviceRequest) => devicesApi.create(r),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useUpdateDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: number; request: UpdateDeviceRequest }) =>
      devicesApi.update(id, request),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useDeactivateDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => devicesApi.deactivate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}
